'use client';

import React from 'react';
import { FileText, CheckCircle2, Eye, XCircle } from 'lucide-react';
import { KycItem } from '../types';

interface KycTabProps {
  kycList: KycItem[];
  rejectionReason: { [id: string]: string };
  setRejectionReason: (val: { [id: string]: string }) => void;
  submittingId: string | null;
  handleReviewKyc: (kycId: string, status: 'APPROVED' | 'REJECTED') => void;
  setSelectedDocUrl: (url: string | null) => void;
}

export const KycTab: React.FC<KycTabProps> = ({
  kycList,
  rejectionReason,
  setRejectionReason,
  submittingId,
  handleReviewKyc,
  setSelectedDocUrl,
}) => {
  return (
    <div className="bg-[#0A0D1A] border border-slate-800/80 rounded-3xl p-6 sm:p-8 shadow-2xl space-y-6">
      <div>
        <h2 className="text-lg font-black text-white flex items-center gap-2">
          <FileText className="w-5 h-5 text-amber-400" />
          <span>Organization Verification Center</span>
        </h2>
        <p className="text-xs text-slate-400 mt-1">Review organization applications and identity proofs to grant platform permissions.</p>
      </div>

      {kycList.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-slate-800/80 rounded-3xl">
          <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3 opacity-60" />
          <h3 className="text-base font-bold text-white">Verification Queue Empty</h3>
          <p className="text-xs text-slate-400 mt-1">There are no pending organization KYC applications requiring approval.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {kycList.map((item) => (
            <div key={item.id} className="bg-[#060811] border border-slate-800 rounded-2xl p-6 hover:border-slate-700 transition-all">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <h3 className="text-base font-bold text-white">{item.companyName}</h3>
                    <span className="px-2.5 py-0.5 bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 text-[10px] font-extrabold rounded-md uppercase">
                      Team Size: {item.teamSize}
                    </span>
                  </div>

                  <div className="text-xs text-slate-400 flex flex-wrap items-center gap-4">
                    <span>Applicant: <strong className="text-slate-200">{item.user?.name}</strong> ({item.user?.email})</span>
                    <span>•</span>
                    <span>ID Type: <strong className="text-slate-200">{item.idType}</strong></span>
                    <span>•</span>
                    <span>ID Number: <strong className="text-slate-200 font-mono">{item.idNumber}</strong></span>
                  </div>

                  {item.documentUrl && (
                    <div className="pt-2">
                      <button
                        onClick={() => setSelectedDocUrl(item.documentUrl)}
                        className="inline-flex items-center gap-1.5 text-xs text-indigo-400 hover:text-indigo-300 font-semibold cursor-pointer underline underline-offset-4"
                      >
                        <Eye className="w-3.5 h-3.5" />
                        <span>Inspect Document Attachment</span>
                      </button>
                    </div>
                  )}
                </div>

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 shrink-0">
                  <input
                    type="text"
                    placeholder="Rejection reason..."
                    value={rejectionReason[item.id] || ''}
                    onChange={(e) => setRejectionReason({ ...rejectionReason, [item.id]: e.target.value })}
                    className="bg-[#0A0D1A] border border-slate-800 rounded-xl px-3.5 py-2 text-xs text-slate-200 placeholder-slate-600 focus:outline-none focus:border-indigo-500"
                  />

                  <button
                    onClick={() => handleReviewKyc(item.id, 'REJECTED')}
                    disabled={submittingId === item.id}
                    className="px-4 py-2 bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/30 text-rose-400 font-extrabold text-xs rounded-xl flex items-center justify-center gap-1.5 transition-all cursor-pointer"
                  >
                    <XCircle className="w-4 h-4" />
                    <span>Reject</span>
                  </button>

                  <button
                    onClick={() => handleReviewKyc(item.id, 'APPROVED')}
                    disabled={submittingId === item.id}
                    className="px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:opacity-90 text-white font-extrabold text-xs rounded-xl shadow-lg shadow-emerald-600/20 flex items-center justify-center gap-1.5 transition-all cursor-pointer"
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    <span>Approve Organization</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
